import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, ImagePlus, LogOut, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg } from "@/lib/api";
import { DEFAULT_CONTENT, SLOT_LABELS, SLOTS } from "@/lib/content";
import { LogoLockup } from "@/components/LogoMark";

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await api("/auth/login", { method: "POST", body: { email, password } });
      toast.success("¡Bienvenido de vuelta!");
      onLogin(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl border border-linelight bg-navydeep/60 p-8 text-paper shadow-2xl backdrop-blur"
      >
        <LogoLockup light />
        <h1 className="mt-6 font-display text-3xl font-semibold">Panel de administración</h1>
        <p className="mt-1 text-sm text-paper/60">Cambia textos, imágenes y revisa solicitudes.</p>
        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor="admin-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-paper/60">Correo</label>
            <input id="admin-email" data-testid="admin-login-email" className="field" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@civica.com" />
          </div>
          <div className="relative">
            <label htmlFor="admin-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-paper/60">Contraseña</label>
            <input id="admin-password" data-testid="admin-login-password" type={show ? "text" : "password"} className="field pr-11" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-[2.4rem] text-paper/50 hover:text-paper" aria-label="Mostrar contraseña">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={busy}
          className="mt-6 w-full rounded-full bg-heritage px-6 py-3.5 text-sm font-bold text-paper transition-all hover:bg-heritagedark disabled:opacity-60"
        >
          {busy ? "Entrando…" : "Entrar"}
        </button>
      </motion.form>
    </div>
  );
}

const asL = (v) => (typeof v === "string" ? { es: v, en: "" } : v || { es: "", en: "" });

function LangBadge({ children }) {
  return <span className="mr-2 inline-block rounded-md bg-navy/5 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-mutedtext">{children}</span>;
}

function TextsTab({ content, setContent, refresh }) {
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const updated = await api("/admin/content", { method: "PUT", body: content });
      setContent((c) => ({ ...c, ...updated }));
      toast.success("Contenido actualizado. La página ya lo muestra.");
      refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const LField = ({ label, k, testid, textarea }) => {
    const val = asL(content[k]);
    const upd = (langKey, value) =>
      setContent((c) => ({ ...c, [k]: { ...asL(c[k]), [langKey]: value } }));
    return (
      <div className="rounded-xl border border-line bg-white p-4">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-mutedtext">{label}</p>
        <div className="space-y-2">
          <div>
            <LangBadge>ES</LangBadge>
            {textarea ? (
              <textarea rows={2} data-testid={`${testid}-es`} className="field mt-1" value={val.es} onChange={(e) => upd("es", e.target.value)} />
            ) : (
              <input data-testid={`${testid}-es`} className="field mt-1" value={val.es} onChange={(e) => upd("es", e.target.value)} />
            )}
          </div>
          <div>
            <LangBadge>EN</LangBadge>
            {textarea ? (
              <textarea rows={2} data-testid={`${testid}-en`} className="field mt-1" value={val.en} onChange={(e) => upd("en", e.target.value)} placeholder="English version…" />
            ) : (
              <input data-testid={`${testid}-en`} className="field mt-1" value={val.en} onChange={(e) => upd("en", e.target.value)} placeholder="English version…" />
            )}
          </div>
        </div>
      </div>
    );
  };

  const LLineField = ({ label, i, testid }) => {
    const val = asL((content.hero_title_lines || [])[i]);
    const upd = (langKey, value) =>
      setContent((c) => ({
        ...c,
        hero_title_lines: c.hero_title_lines.map((l, j) => (j === i ? { ...asL(l), [langKey]: value } : asL(l))),
      }));
    return (
      <div className="rounded-xl border border-line bg-white p-4">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-mutedtext">{label}</p>
        <LangBadge>ES</LangBadge>
        <input data-testid={`${testid}-es`} className="field mt-1" value={val.es} onChange={(e) => upd("es", e.target.value)} />
        <div className="mt-2">
          <LangBadge>EN</LangBadge>
          <input data-testid={`${testid}-en`} className="field mt-1" value={val.en} onChange={(e) => upd("en", e.target.value)} placeholder="English version…" />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-tab-texts">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-line bg-white p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-mutedtext">Nombre del negocio (igual en ambos idiomas)</p>
          <input data-testid="admin-field-brand" className="field" value={content.brand_name} onChange={(e) => set("brand_name", e.target.value)} />
        </div>
        <div className="rounded-xl border border-line bg-white p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-mutedtext">Número de WhatsApp (con país, solo números)</p>
          <input data-testid="admin-field-whatsapp" className="field" value={content.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="13051234567" />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <LField label="Subtítulo de marca" k="brand_suffix" testid="admin-field-suffix" />
        <LField label="Frase superior del hero" k="hero_eyebrow" testid="admin-field-eyebrow" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <LLineField label="Título del hero — línea 1" i={0} testid="admin-field-title1" />
        <LLineField label="Título del hero — línea 2" i={1} testid="admin-field-title2" />
      </div>
      <LField label="Subtítulo del hero" k="hero_subtitle" testid="admin-field-subtitle" textarea />
      <div className="grid gap-4 sm:grid-cols-3">
        <LField label="Botón principal" k="hero_cta_primary" testid="admin-field-cta1" />
        <LField label="Botón secundario" k="hero_cta_secondary" testid="admin-field-cta2" />
        <LField label="Sello de confianza" k="hero_trust" testid="admin-field-trust" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-line bg-white p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-mutedtext">Precio actual (USD)</p>
          <input data-testid="admin-field-price" className="field" value={content.price_current} onChange={(e) => set("price_current", e.target.value)} />
        </div>
        <div className="rounded-xl border border-line bg-white p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-mutedtext">Precio anterior (USD)</p>
          <input data-testid="admin-field-price-old" className="field" value={content.price_original} onChange={(e) => set("price_original", e.target.value)} />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <LField label="Etiqueta de oferta" k="price_badge" testid="admin-field-badge" />
        <LField label="Nota del precio" k="price_note" testid="admin-field-price-note" />
      </div>

      <div className="rounded-2xl border border-line bg-white p-5" data-testid="admin-faqs">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-navy">Preguntas frecuentes (español e inglés)</p>
          <button
            data-testid="admin-faq-add"
            onClick={() => setContent((c) => ({ ...c, faqs: [...c.faqs, { q: { es: "Nueva pregunta", en: "New question" }, a: { es: "Respuesta", en: "Answer" } }] }))}
            className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-1.5 text-xs font-bold text-navy hover:bg-paperdeep"
          >
            <Plus size={13} /> Agregar
          </button>
        </div>
        <div className="mt-4 space-y-4">
          {content.faqs.map((f, i) => {
            const q = asL(f.q);
            const a = asL(f.a);
            const updFaq = (field, langKey, value) =>
              setContent((c) => ({ ...c, faqs: c.faqs.map((x, j) => (j === i ? { ...x, [field]: { ...asL(x[field]), [langKey]: value } } : x)) }));
            return (
              <div key={i} className="rounded-xl bg-paperdeep/60 p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <div>
                      <LangBadge>ES</LangBadge>
                      <input data-testid={`admin-faq-q-${i}-es`} className="field mt-1" value={q.es} onChange={(e) => updFaq("q", "es", e.target.value)} />
                    </div>
                    <div>
                      <LangBadge>EN</LangBadge>
                      <input data-testid={`admin-faq-q-${i}-en`} className="field mt-1" value={q.en} onChange={(e) => updFaq("q", "en", e.target.value)} />
                    </div>
                  </div>
                  <button
                    data-testid={`admin-faq-remove-${i}`}
                    onClick={() => setContent((c) => ({ ...c, faqs: c.faqs.filter((_, j) => j !== i) }))}
                    className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line text-heritage hover:bg-heritage/10"
                    aria-label="Eliminar pregunta"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  <div>
                    <LangBadge>ES</LangBadge>
                    <textarea rows={2} data-testid={`admin-faq-a-${i}-es`} className="field mt-1" value={a.es} onChange={(e) => updFaq("a", "es", e.target.value)} />
                  </div>
                  <div>
                    <LangBadge>EN</LangBadge>
                    <textarea rows={2} data-testid={`admin-faq-a-${i}-en`} className="field mt-1" value={a.en} onChange={(e) => updFaq("a", "en", e.target.value)} placeholder="English version…" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <button
        onClick={save}
        data-testid="admin-save-content"
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-bold text-paper transition-all hover:-translate-y-0.5 disabled:opacity-60"
      >
        <Save size={15} /> {busy ? "Guardando…" : "Guardar cambios"}
      </button>
    </div>
  );
}

function ImagesTab({ content, setContent, refresh }) {
  const [busySlot, setBusySlot] = useState("");

  const upload = (slotId, file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result);
      const b64 = dataUrl.split(",")[1];
      setBusySlot(slotId);
      try {
        await api(`/admin/images/${slotId}`, { method: "POST", body: { filename: file.name, content_type: file.type || "image/jpeg", data: b64 } });
        setContent((c) => ({ ...c, images: { ...c.images, [slotId]: { ...c.images[slotId], url: `/api/images/${slotId}` } } }));
        toast.success("Imagen actualizada.");
        refresh();
      } catch (err) {
        toast.error(err.message);
      } finally {
        setBusySlot("");
      }
    };
    reader.readAsDataURL(file);
  };

  const remove = async (slotId) => {
    setBusySlot(slotId);
    try {
      await api(`/admin/images/${slotId}`, { method: "DELETE" });
      setContent((c) => ({ ...c, images: { ...c.images, [slotId]: { ...c.images[slotId], url: "" } } }));
      toast.success("Imagen quitada. Se muestra la original.");
      refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusySlot("");
    }
  };

  const toggle = (slotId, visible) => {
    setContent((c) => ({ ...c, images: { ...c.images, [slotId]: { ...c.images[slotId], visible } } }));
  };

  return (
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="admin-tab-images">
      {SLOTS.map((slotId) => {
        const img = content.images?.[slotId] || { url: "", visible: true };
        return (
          <div key={slotId} data-testid={`admin-image-slot-${slotId}`} className="overflow-hidden rounded-2xl border border-line bg-white">
            <div className="relative h-40 bg-paperdeep">
              {img.url ? (
                <img src={resolveImg(img.url)} alt={SLOT_LABELS[slotId]} className={`h-full w-full object-cover ${img.visible ? "" : "opacity-30"}`} />
              ) : (
                <div className="flex h-full items-center justify-center text-xs font-semibold text-mutedtext">Sin imagen personalizada</div>
              )}
              {!img.visible && (
                <span className="absolute right-3 top-3 rounded-full bg-heritage px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-paper">Oculta</span>
              )}
            </div>
            <div className="p-4">
              <p className="text-sm font-bold text-navy">{SLOT_LABELS[slotId]}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-navy px-3.5 py-2 text-xs font-bold text-paper hover:opacity-90">
                  <ImagePlus size={13} /> {busySlot === slotId ? "Subiendo…" : "Subir"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid={`admin-upload-${slotId}`}
                    onChange={(e) => e.target.files?.[0] && upload(slotId, e.target.files[0])}
                  />
                </label>
                {img.url && (
                  <button
                    data-testid={`admin-remove-${slotId}`}
                    onClick={() => remove(slotId)}
                    disabled={busySlot === slotId}
                    className="inline-flex items-center gap-1.5 rounded-full border border-heritage/40 px-3.5 py-2 text-xs font-bold text-heritage hover:bg-heritage/10 disabled:opacity-50"
                  >
                    <Trash2 size={13} /> Quitar
                  </button>
                )}
                <button
                  data-testid={`admin-visibility-${slotId}`}
                  onClick={() => toggle(slotId, !img.visible)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-bold text-navy hover:bg-paperdeep"
                >
                  <Eye size={13} /> {img.visible ? "Ocultar" : "Mostrar"}
                </button>
              </div>
            </div>
          </div>
        );
      })}
      <p className="rounded-2xl border border-line bg-white p-4 text-xs leading-relaxed text-mutedtext sm:col-span-2 xl:col-span-3">
        Los cambios de visibilidad se guardan con el botón “Guardar cambios” de la pestaña Textos. Subir o quitar imágenes se aplica de inmediato.
      </p>
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState(null);
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    api("/admin/leads")
      .then(setLeads)
      .catch(() => setLeads([]));
    api("/admin/payments")
      .then(setPayments)
      .catch(() => setPayments([]));
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-tab-leads">
      <div className="overflow-hidden rounded-2xl border border-line bg-white" data-testid="admin-payments">
        <div className="flex items-center justify-between border-b border-line bg-paperdeep/70 px-5 py-3">
          <p className="text-sm font-bold text-navy">Inscripciones pagadas (Stripe)</p>
          <span className="font-mono text-xs text-mutedtext">{payments ? payments.filter((p) => p.payment_status === "paid").length : "…"} pagadas</span>
        </div>
        {!payments ? (
          <p className="p-5 text-sm text-mutedtext">Cargando pagos…</p>
        ) : payments.length === 0 ? (
          <p className="p-6 text-sm text-mutedtext">Cuando alguien compre el curso, el pago aparecerá aquí automáticamente.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-paperdeep/40 text-xs uppercase tracking-wider text-mutedtext">
              <tr>
                <th className="px-5 py-2.5">Sesión</th>
                <th className="px-5 py-2.5">Correo</th>
                <th className="px-5 py-2.5">Monto</th>
                <th className="px-5 py-2.5">Estado</th>
                <th className="px-5 py-2.5">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {payments.map((p, i) => (
                <tr key={p.session_id} data-testid={`admin-payment-row-${i}`}>
                  <td className="px-5 py-3 font-mono text-[11px] text-mutedtext">{p.session_id?.slice(0, 18)}…</td>
                  <td className="px-5 py-3 text-inkbody">{p.customer_email || "—"}</td>
                  <td className="px-5 py-3 font-bold text-navy">${((p.amount || 0) / 100).toFixed(2)} {p.currency?.toUpperCase()}</td>
                  <td className="px-5 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${p.payment_status === "paid" ? "bg-emeraldsucc/10 text-emeraldsucc" : "bg-paperdeep text-mutedtext"}`}>
                      {p.payment_status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-mutedtext">{p.created_at ? new Date(p.created_at).toLocaleString("es-US") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-line bg-white">
        <div className="border-b border-line bg-paperdeep/70 px-5 py-3">
          <p className="text-sm font-bold text-navy">Solicitudes del formulario</p>
        </div>
        {!leads ? (
          <p className="p-5 text-sm text-mutedtext">Cargando solicitudes…</p>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-display text-2xl text-navy">Aún no hay solicitudes</p>
            <p className="mt-1 text-sm text-mutedtext">Cuando alguien se inscriba en la página, aparecerá aquí.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-paperdeep/40 text-xs uppercase tracking-wider text-mutedtext">
              <tr>
                <th className="px-5 py-3">Nombre</th>
                <th className="px-5 py-3">Correo</th>
                <th className="px-5 py-3">Teléfono</th>
                <th className="px-5 py-3">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {leads.map((l, i) => (
                <tr key={i} data-testid={`admin-lead-row-${i}`}>
                  <td className="px-5 py-3.5 font-bold text-navy">{l.nombre}</td>
                  <td className="px-5 py-3.5 text-inkbody">{l.email}</td>
                  <td className="px-5 py-3.5 text-inkbody">{l.telefono || "—"}</td>
                  <td className="px-5 py-3.5 text-mutedtext">{new Date(l.created_at).toLocaleString("es-US")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("textos");
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const loadedRef = useRef(false);

  const loadContent = async () => {
    try {
      const data = await api("/content");
      setContent((c) => ({ ...c, ...data }));
    } catch (e) {
      /* keep defaults */
    }
  };

  useEffect(() => {
    api("/auth/me").then(setUser).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user && !loadedRef.current) {
      loadedRef.current = true;
      loadContent();
    }
  }, [user]);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-brass">Cargando…</p>
      </div>
    );
  }

  if (!user) return <LoginCard onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-paperdeep" data-testid="admin-dashboard">
      <header className="sticky top-0 z-50 border-b border-line bg-paper/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
          <LogoLockup brand={content.brand_name} suffix="Panel de administración" />
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs font-bold text-mutedtext hover:text-navy">Ver página ↗</a>
            <button
              data-testid="admin-logout"
              onClick={async () => {
                await api("/auth/logout", { method: "POST" });
                setUser(null);
                toast.success("Sesión cerrada.");
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-bold text-navy hover:bg-white"
            >
              <LogOut size={13} /> Salir
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl gap-1 px-5 sm:px-8">
          {[
            ["textos", "Textos e información"],
            ["imagenes", "Imágenes"],
            ["solicitudes", "Solicitudes"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`-mb-px border-b-2 px-4 py-3 text-sm font-bold transition-colors ${
                tab === id ? "border-heritage text-heritage" : "border-transparent text-mutedtext hover:text-navy"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        {tab === "textos" && <TextsTab content={content} setContent={setContent} refresh={loadContent} />}
        {tab === "imagenes" && <ImagesTab content={content} setContent={setContent} refresh={loadContent} />}
        {tab === "solicitudes" && <LeadsTab />}
      </main>
    </div>
  );
}
